import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  IoAddOutline, IoTrashOutline, IoDocumentAttachOutline, IoCreateOutline, IoClose, IoCloudUploadOutline,
  IoEyeOutline, IoCheckmarkCircle, IoArrowBack, IoColorPaletteOutline, IoChevronDown, IoChevronForward,
  IoLinkOutline, IoOpenOutline, IoDocumentTextOutline, IoFolderOpenOutline, IoReorderThreeOutline, IoSearchOutline, IoArrowRedoOutline, IoChatbubblesOutline, IoMusicalNotesOutline, IoLogoYoutube, IoShareSocialOutline, IoCopyOutline,
} from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { CARNET_LAYOUTS, layoutStyles } from "@/lib/carnetThemes";
import { THEMES, FEATURED_THEME_KEYS } from "@/lib/themes";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import {
  getCarnet, updateCarnetSettings, addCarnetCategory, updateCarnetCategory,
  deleteCarnetCategory, addCarnetEntry, updateCarnetEntry, deleteCarnetEntry, uploadCarnetFile,
  openCarnetFile, carnetFileUrl, adminViewCarnet, adminRespondCarnet, getPageConfig, markCarnetResponsesSeen, moveCarnetEntry, reorderCarnetEntries,
  createShare, listMembers, sharesWithMeCount,
  getCarnetAccessFor, requestCarnetAccess, revokeCarnetAccess,
  myCarnetAccessRequests, respondCarnetAccess,
  accessAddEntry, accessUpdateEntry, accessDeleteEntry,
  accessAddCategory, accessUpdateCategory, accessDeleteCategory,
} from "@/lib/api";

const ACCENTS = ["#e11d2a", "#ff7a00", "#ffd400", "#00e5a0", "#4d8dff", "#8b5cf6", "#ff4dcb", "#14b8a6", "#64748b"];
const EMOJIS = ["📁", "🎯", "🧑", "🎤", "🏛️", "🌌", "📝", "💡", "🔥", "🌊", "🎭", "🧵", "🌱", "⚔️", "📚", "⭐", "✶", "🗂️"];

const IMG_EXT = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "heic"];
const domainOf = (url) => { try { return new URL(/^https?:\/\//.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, ""); } catch { return ""; } };
const linkLabel = (d) => {
  const m = { "canva.com": "Canva", "docs.google.com": "Google Docs", "drive.google.com": "Google Drive", "sheets.google.com": "Google Sheets", "slides.google.com": "Google Slides", "notion.so": "Notion", "figma.com": "Figma", "youtube.com": "YouTube", "youtu.be": "YouTube", "dropbox.com": "Dropbox", "genial.ly": "Genially", "genially.com": "Genially", "wattpad.com": "Wattpad" };
  return m[d] || (d ? d.split(".")[0].replace(/^\w/, (c) => c.toUpperCase()) : "Lien");
};
const faviconUrl = (d) => `https://www.google.com/s2/favicons?domain=${d}&sz=64`;
const fileExt = (name) => { const p = (name || "").split("."); return p.length > 1 ? p.pop().toUpperCase() : "FICHIER"; };
const isImg = (name) => IMG_EXT.includes(((name || "").split(".").pop() || "").toLowerCase());

const reviewChip = (e) => {
  if (e.review_closed) return { label: "Terminée", bg: "#e8f7ec", fg: "#1a7f37" };
  if (e.sensei_response) return { label: "En cours", bg: "color-mix(in srgb, var(--brand) 14%, transparent)", fg: RED };
  return { label: "En attente", bg: "#fff3e0", fg: "#b26a00" };
};

const AUDIO_EXT = ["mp3", "wav", "m4a", "ogg", "aac"];
const isAudio = (name) => AUDIO_EXT.includes(((name || "").split(".").pop() || "").toLowerCase());

const ytId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

function CarnetAudio({ fileId, label, testid }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true, created = null;
    carnetFileUrl(fileId).then((u) => { if (live) { created = u; setUrl(u); } }).catch(() => setErr(true));
    return () => { live = false; if (created) URL.revokeObjectURL(created); };
  }, [fileId]);
  return (
    <div style={audioSx.wrap} data-testid={testid ? `${testid}-wrap` : undefined}>
      <div style={audioSx.head}>
        <span style={audioSx.icon}><IoMusicalNotesOutline size={18} /></span>
        <span style={audioSx.name}>{label || "Enregistrement audio"}</span>
      </div>
      {err ? <div style={audioSx.msg}>Audio indisponible</div>
        : !url ? <div style={audioSx.msg}>Chargement…</div>
        : <audio controls src={url} style={audioSx.player} data-testid={testid} />}
    </div>
  );
}

function CarnetImage({ fileId, filename, testid, onOpen }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true, created = null;
    carnetFileUrl(fileId).then((u) => { if (live) { created = u; setUrl(u); } }).catch(() => setErr(true));
    return () => { live = false; if (created) URL.revokeObjectURL(created); };
  }, [fileId]);
  if (err) {
    return <div style={imgSx.fallback} data-testid={testid ? `${testid}-err` : undefined}>Image indisponible</div>;
  }
  return (
    <button style={imgSx.wrap} data-testid={testid} onClick={() => url && onOpen && onOpen(url, filename)} title="Agrandir">
      {!url ? <div style={imgSx.loading} className="td-skeleton" /> : <img src={url} alt={filename || "Image"} style={imgSx.img} loading="lazy" />}
      <span style={imgSx.cap}>{filename || "Image"} · agrandir</span>
    </button>
  );
}

const imgSx = {
  wrap: { marginTop: 10, width: "100%", padding: 0, border: "1px solid var(--border-c)", borderRadius: 14, overflow: "hidden", cursor: "zoom-in", background: "var(--bg-tertiary)", display: "block" },
  img: { display: "block", width: "100%", maxHeight: 240, objectFit: "cover" },
  loading: { width: "100%", height: 160 },
  cap: { display: "block", padding: "8px 12px", fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", textAlign: "left" },
  fallback: { marginTop: 10, padding: 12, borderRadius: 12, border: "1px dashed var(--border-c)", color: "var(--text-secondary)", fontSize: 13 },
};


const audioSx = {
  wrap: { marginTop: 10, padding: 12, borderRadius: 14, background: "linear-gradient(135deg, color-mix(in srgb, var(--brand) 10%, transparent), var(--bg-tertiary))", border: "1px solid var(--border-c)" },
  head: { display: "flex", alignItems: "center", gap: 9, marginBottom: 9 },
  icon: { width: 34, height: 34, borderRadius: 10, background: RED, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  name: { fontSize: 13.5, fontWeight: 800, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  player: { width: "100%", height: 40 },
  msg: { fontSize: 13, color: "var(--text-secondary)", padding: "6px 2px" },
};

import VoiceRecorder from "@/components/VoiceRecorder";
import YouTubeEmbed from "@/components/YouTubeEmbed";
export default function MonCarnet() {
  const { userId } = useParams();
  const isAdminView = !!userId;
  const { user } = useAuth();
  const { theme, setTheme } = useApp();
  const nav = useNavigate();
  const [fileDrag, setFileDrag] = useState(false);
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [editor, setEditor] = useState(null);
  const [catEditor, setCatEditor] = useState(null);
  const [personalize, setPersonalize] = useState(false);
  const [busy, setBusy] = useState(false);
  const [respondTo, setRespondTo] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [dragOverEntry, setDragOverEntry] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [query, setQuery] = useState("");
  const [shareFor, setShareFor] = useState(null);
  const [shareCfg, setShareCfg] = useState({ mode: "member", member_id: "", permission: "view" });
  const [members, setMembers] = useState([]);
  const [shareResult, setShareResult] = useState(null);
  const [shareUnseen, setShareUnseen] = useState(0);
  const [access, setAccess] = useState(null);        // vue admin : état de mon accès au carnet du membre
  const [accessReqs, setAccessReqs] = useState([]);  // vue membre : demandes/accès me concernant
  const fileRef = useRef(null);
  const coverRef = useRef(null);

  const load = async () => {
    try {
      const d = isAdminView ? await adminViewCarnet(userId) : await getCarnet();
      setData(d);
      setActive((cur) => {
        if (cur && (d.categories || []).some((c) => c.id === cur)) return cur;
        const t = (d.categories || []).filter((c) => !c.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
        return t[0]?.id || null;
      });
    } catch { toast.error("Chargement du carnet impossible"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);
  const loadAccess = async () => {
    try {
      if (isAdminView) setAccess(await getCarnetAccessFor(userId));
      else setAccessReqs(await myCarnetAccessRequests());
    } catch { /* silencieux */ }
  };
  useEffect(() => { loadAccess(); /* eslint-disable-next-line */ }, [userId, isAdminView]);
  useEffect(() => {
    if (isAdminView) return;
    sharesWithMeCount().then((r) => setShareUnseen(r.count || 0)).catch(() => {});
    listMembers().then(setMembers).catch(() => {});
  }, [isAdminView]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("reply");
    if (p && !userId) markCarnetResponsesSeen().catch(() => {});
  }, [userId]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  const themeKeyEarly = data?.settings?.theme || "sumi";
  useEffect(() => {}, [themeKeyEarly]);

  if (!data) return <DojoShell crumbs={[{ label: "Mon Carnet" }]}><p style={{ color: "var(--text-secondary)" }}>Chargement…</p></DojoShell>;

  const st = data.settings || {};
  const accent = st.accent || RED;
  const layout = st.layout || "grid";
  const { gridStyle, cardExtra } = layoutStyles(layout);

  const handleExternalDrop = async (ev) => {
    setFileDrag(false);
    if (readOnly) return;
    const files = Array.from(ev.dataTransfer?.files || []);
    if (!files.length) return;
    ev.preventDefault();
    const activeCat2 = categories.find((c) => c.id === active);
    if (activeCat2?.kind === "exercises") { toast.error("Impossible de déposer ici — choisis un autre dossier"); return; }
    setBusy(true);
    try {
      for (const f of files) {
        const up = await uploadCarnetFile(f);
        const audio = isAudio(up.filename) || (f.type || "").startsWith("audio/");
        await apiAddEntry({ section: active, title: f.name, body: "", kind: audio ? "audio" : "file", file_id: up.file_id, filename: up.filename });
      }
      toast.success(files.length > 1 ? `${files.length} fichiers ajoutés` : "Fichier ajouté");
      await load();
    } catch { toast.error("Dépôt impossible (fichier trop lourd ?)"); } finally { setBusy(false); }
  };
  const onWrapDragOver = (ev) => { if (!readOnly && ev.dataTransfer?.types?.includes("Files")) { ev.preventDefault(); setFileDrag(true); } };
  const readOnly = isAdminView && !(access?.status === "granted");
  const canEditMember = isAdminView && access?.status === "granted";
  const em = canEditMember ? userId : null;
  const apiAddEntry = (p) => (em ? accessAddEntry(em, p) : addCarnetEntry(p));
  const apiUpdateEntry = (id, p) => (em ? accessUpdateEntry(em, id, p) : updateCarnetEntry(id, p));
  const apiDeleteEntry = (id) => (em ? accessDeleteEntry(em, id) : deleteCarnetEntry(id));
  const apiAddCat = (p) => (em ? accessAddCategory(em, p) : addCarnetCategory(p));
  const apiUpdateCat = (id, p) => (em ? accessUpdateCategory(em, id, p) : updateCarnetCategory(id, p));
  const apiDeleteCat = (id) => (em ? accessDeleteCategory(em, id) : deleteCarnetCategory(id));
  const categories = data.categories || [];
  const tops = categories.filter((c) => !c.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
  const childrenOf = (pid) => categories.filter((c) => c.parent_id === pid).sort((a, b) => (a.order || 0) - (b.order || 0));
  const activeCat = categories.find((c) => c.id === active) || tops[0];
  const activeAccent = activeCat?.color || accent;
  const respondedUnseen = readOnly ? [] : (data.entries || []).filter((e) => e.sensei_response && e.student_seen === false);

  const saveSettings = async (patch) => {
    const next = { ...st, ...patch };
    setData((d) => ({ ...d, settings: next }));
    try { await updateCarnetSettings(patch); } catch { toast.error("Échec de la personnalisation"); }
  };
  /* ---- Accès complet (admin ⇄ membre) ---- */
  const askAccess = async () => {
    try { const a = await requestCarnetAccess(userId); setAccess(a); toast.success("Demande envoyée au membre"); }
    catch { toast.error("Impossible d'envoyer la demande"); }
  };
  const endAccess = async () => {
    if (!access?.id) return;
    if (!window.confirm("Mettre fin à ton accès complet à ce carnet ?")) return;
    try { await revokeCarnetAccess(access.id); await loadAccess(); await load(); toast.success("Accès retiré"); }
    catch { toast.error("Échec"); }
  };
  const answerAccess = async (rid, approve) => {
    try { await respondCarnetAccess(rid, approve); await loadAccess(); toast.success(approve ? "Accès accordé" : "Demande refusée"); }
    catch { toast.error("Échec"); }
  };
  const memberRevoke = async (rid) => {
    if (!window.confirm("Retirer l'accès complet accordé à l'administrateur ?")) return;
    try { await revokeCarnetAccess(rid); await loadAccess(); toast.success("Accès retiré"); }
    catch { toast.error("Échec"); }
  };

  const entries = (data.entries || []).filter((e) => e.section === active);
  const exercises = activeCat?.kind === "exercises"
    ? (data.exercises || []).filter((e) => !activeCat.porte_index || e.porte_index === activeCat.porte_index)
    : [];
  const q = query.trim().toLowerCase();
  const searchResults = q ? (data.entries || []).filter((e) =>
    (e.title || "").toLowerCase().includes(q) || (e.body || "").toLowerCase().includes(q) ||
    (e.url || "").toLowerCase().includes(q) || (e.filename || "").toLowerCase().includes(q)) : null;
  const catName = (id) => categories.find((c) => c.id === id)?.label || "Dossier";

  /* ---- Catégories ---- */
  const openNewCat = (parent_id = null) => setCatEditor({ mode: "create", data: { label: "", icon: "📁", color: parent_id ? (categories.find((c) => c.id === parent_id)?.color || accent) : accent, parent_id } });
  const openEditCat = (c) => setCatEditor({ mode: "edit", data: { ...c } });
  const saveCat = async () => {
    const d = catEditor.data;
    if (!d.label?.trim()) { toast.error("Donne un nom au dossier"); return; }
    setBusy(true);
    try {
      if (catEditor.mode === "edit") await apiUpdateCat(d.id, { label: d.label.trim(), icon: d.icon, color: d.color });
      else { const c = await apiAddCat({ label: d.label.trim(), icon: d.icon, color: d.color, parent_id: d.parent_id }); if (d.parent_id) setExpanded((e) => ({ ...e, [d.parent_id]: true })); setActive(c.id); }
      setCatEditor(null); await load(); toast.success("Dossier enregistré");
    } catch { toast.error("Échec"); } finally { setBusy(false); }
  };
  const removeCategory = async (c) => {
    if (c.kind === "exercises") { toast.error("Le dossier Exercices est lié au Chemin des 7 Portes et ne peut pas être supprimé."); return; }
    const subs = childrenOf(c.id).length;
    if (!window.confirm(`Supprimer « ${c.label} »${subs ? ` et ses ${subs} sous-dossier(s)` : ""} ? Les entrées liées seront supprimées.`)) return;
    try { await apiDeleteCat(c.id); setActive(null); await load(); } catch { toast.error("Échec"); }
  };

  /* ---- Entrées ---- */
  const openNew = () => setEditor({ section: active, title: "", body: "", kind: "note", file_id: null, filename: null, url: "", review_requested: false });
  const openEdit = (e) => setEditor({ ...e, kind: e.kind || "note", url: e.url || "" });
  const saveEntry = async () => {
    const k = editor.kind || "note";
    if (!editor.title.trim()) { toast.error("Donne un titre à ton entrée"); return; }
    if (k === "link" && !(editor.url || "").trim()) { toast.error("Colle le lien (Canva, Google Docs…)"); return; }
    if (k === "file" && !editor.file_id) { toast.error("Joins un fichier"); return; }
    if (k === "audio" && !editor.file_id) { toast.error("Joins un fichier audio (MP3, WAV…)"); return; }
    if (k === "video" && !ytId(editor.video_url || "")) { toast.error("Colle un lien YouTube valide"); return; }
    setBusy(true);
    try {
      const payload = { section: editor.section, title: editor.title, body: editor.body || "", kind: k,
        file_id: (k === "file" || k === "audio") ? editor.file_id : null, filename: (k === "file" || k === "audio") ? editor.filename : null,
        url: k === "link" ? editor.url.trim() : null, video_url: k === "video" ? editor.video_url.trim() : null,
        review_requested: editor.review_requested };
      if (editor.id) await apiUpdateEntry(editor.id, payload); else await apiAddEntry(payload);
      setEditor(null); await load(); toast.success(em ? "Enregistré dans le carnet du membre" : "Enregistré dans ton carnet");
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const removeEntry = async (id) => { if (!window.confirm("Supprimer cette entrée ?")) return; try { await apiDeleteEntry(id); await load(); } catch { toast.error("Échec"); } };
  const doMove = async (eid, section) => { try { await moveCarnetEntry(eid, section); setMoveFor(null); await load(); toast.success("Document déplacé ✓"); } catch { toast.error("Déplacement impossible"); } };
  const threadOf = (e) => {
    if (e.thread && e.thread.length) return e.thread;
    if (e.sensei_response) return [{ id: "legacy", author: "sensei", name: e.sensei_name || "Sensei", text: e.sensei_response, attachments: e.sensei_attachments || [], created_at: e.responded_at }];
    return [];
  };
  const onFile = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const r = await uploadCarnetFile(f); setEditor((cur) => ({ ...cur, file_id: r.file_id, filename: r.filename })); toast.success("Fichier joint"); }
    catch (e) { toast.error(e?.response?.status === 413 ? "Fichier trop lourd (audio max 60 Mo, autre max 10 Mo)" : "Upload impossible"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const onCoverFile = (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Image trop lourde (max 3 Mo)"); return; }
    const r = new FileReader();
    r.onload = () => setData((d) => ({ ...d, settings: { ...d.settings, cover: r.result } }));
    r.readAsDataURL(f);
    if (coverRef.current) coverRef.current.value = "";
  };
  const openLink = (url) => { const u = /^https?:\/\//.test(url) ? url : `https://${url}`; window.open(u, "_blank", "noopener"); };
  const sendResponse = async (eid) => {
    if (!respondTo?.text?.trim()) { toast.error("Écris ta réponse"); return; }
    setBusy(true);
    try { await adminRespondCarnet(eid, respondTo.text); setRespondTo(null); await load(); toast.success("Retour envoyé à l'élève"); }
    catch { toast.error("Échec de l'envoi"); } finally { setBusy(false); }
  };
  const markSeen = async () => { try { await markCarnetResponsesSeen(); await load(); } catch {} };

  const openShare = (target_type, target_id, title) => {
    setShareResult(null);
    setShareCfg({ mode: "member", member_id: "", permission: "view" });
    setShareFor({ target_type, target_id, title });
  };
  const doShare = async () => {
    if (shareCfg.mode === "member" && !shareCfg.member_id) { toast.error("Choisis un membre"); return; }
    setBusy(true);
    try {
      const res = await createShare({
        target_type: shareFor.target_type, target_id: shareFor.target_id, mode: shareCfg.mode,
        member_id: shareCfg.mode === "member" ? shareCfg.member_id : null, permission: shareCfg.permission,
      });
      if (res.mode === "link" && res.token) { setShareResult(`${window.location.origin}/partage/${res.token}`); toast.success("Lien de partage créé"); }
      else { setShareFor(null); toast.success("Partagé ✓"); }
    } catch { toast.error("Partage impossible"); } finally { setBusy(false); }
  };

  /* ---- Drag & drop d'entrées ---- */
  const onDropTo = async (catId) => {
    setDragOver(null);
    const e = (data.entries || []).find((x) => x.id === dragId);
    setDragId(null);
    if (!e || e.section === catId) return;
    try {
      await updateCarnetEntry(e.id, { section: catId, title: e.title, body: e.body || "", kind: e.kind || "note", file_id: e.file_id || null, filename: e.filename || null, url: e.url || null, video_url: e.video_url || null, review_requested: !!e.review_requested });
      await load(); toast.success("Déplacé");
    } catch { toast.error("Échec du déplacement"); }
  };

  // Réordonner les entrées d'un dossier (glisser une carte sur une autre)
  const onReorderDrop = async (targetId) => {
    const did = dragId;
    setDragOverEntry(null); setDragId(null); setDragOver(null); setDrawerOpen(false);
    if (!did || did === targetId) return;
    const list = (data.entries || []).filter((e) => e.section === active);
    const src = list.find((e) => e.id === did);
    if (!src) return; // la carte traînée n'est pas dans ce dossier → pas un réordonnancement
    const ids = list.map((e) => e.id).filter((id) => id !== did);
    const ti = ids.indexOf(targetId);
    if (ti < 0) return;
    ids.splice(ti, 0, did);
    // Optimiste : réordonne localement tout de suite
    const byId = Object.fromEntries(list.map((e) => [e.id, e]));
    const reordered = ids.map((id, i) => ({ ...byId[id], order: i + 1 }));
    const others = (data.entries || []).filter((e) => e.section !== active);
    setData((d) => ({ ...d, entries: [...reordered, ...others] }));
    try { await reorderCarnetEntries(ids); await load(); } catch { toast.error("Réordonnancement impossible"); await load(); }
  };

  const goFolder = (id) => { setActive(id); setDrawerOpen(false); };

  const dropProps = (id) => ({
    onDragOver: (ev) => { if (dragId) { ev.preventDefault(); setDragOver(id); } },
    onDragLeave: () => setDragOver((o) => (o === id ? null : o)),
    onDrop: () => onDropTo(id),
  });

  const renderEntry = (e, showFolder, reorderable = false) => {
    const acc = categories.find((c) => c.id === e.section)?.color || activeAccent;
    const dom = e.kind === "link" ? domainOf(e.url || "") : "";
    return (
      <div key={e.id} className="ct-card" style={{ ...s.card, ...cardExtra, ...(dragId === e.id ? { opacity: 0.5 } : {}), ...(dragOverEntry === e.id ? s.cardDropOver : {}) }} data-testid={`carnet-entry-${e.id}`}
        draggable={!readOnly} onDragStart={() => { if (!readOnly) { setDragId(e.id); setDrawerOpen(true); } }} onDragEnd={() => { setDragId(null); setDragOver(null); setDragOverEntry(null); }}
        onDragOver={(ev) => { if (reorderable && !readOnly && dragId && dragId !== e.id) { ev.preventDefault(); setDragOverEntry(e.id); } }}
        onDragLeave={() => setDragOverEntry((o) => (o === e.id ? null : o))}
        onDrop={(ev) => { if (reorderable && !readOnly && dragId && dragId !== e.id) { ev.preventDefault(); ev.stopPropagation(); onReorderDrop(e.id); } }}>
        <div style={s.cardHead}>
          <span style={s.entryTitle}>
            <span style={{ marginRight: 8, color: acc, display: "flex" }}>
              {e.kind === "link" ? <IoLinkOutline size={15} /> : e.kind === "audio" ? <IoMusicalNotesOutline size={15} /> : e.kind === "video" ? <IoLogoYoutube size={15} /> : e.kind === "file" ? <IoDocumentTextOutline size={15} /> : e.kind === "exercice" ? <IoCheckmarkCircle size={15} /> : <IoCreateOutline size={15} />}
            </span>
            {e.title}
          </span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {e.review_requested && !e.sensei_response && <span style={{ ...s.pill, background: "#fff3e0", color: "#b26a00" }}><IoEyeOutline size={12} /> En relecture</span>}
            {!readOnly && <button style={s.iconMini} title="Déplacer" onClick={() => setMoveFor(moveFor === e.id ? null : e.id)} data-testid={`carnet-move-${e.id}`}><IoArrowRedoOutline size={15} /></button>}
            {!readOnly && <button style={s.iconMini} title="Partager" onClick={() => openShare("entry", e.id, e.title)} data-testid={`carnet-share-${e.id}`}><IoShareSocialOutline size={15} /></button>}
            {!readOnly && <button style={s.iconMini} onClick={() => openEdit(e)} data-testid={`carnet-edit-${e.id}`}><IoCreateOutline size={15} /></button>}
            {!readOnly && <button style={{ ...s.iconMini, color: RED }} onClick={() => removeEntry(e.id)} data-testid={`carnet-del-${e.id}`}><IoTrashOutline size={14} /></button>}
          </div>
        </div>
        {moveFor === e.id && !readOnly && (
          <div style={s.movePanel} data-testid={`carnet-move-panel-${e.id}`}>
            <div style={s.moveTitle}>Déplacer vers…</div>
            <div style={s.moveList}>
              {tops.map((t) => (
                <React.Fragment key={t.id}>
                  <button style={{ ...s.moveOpt, ...(e.section === t.id ? s.moveOptActive : {}), fontWeight: 800 }} disabled={e.section === t.id} onClick={() => doMove(e.id, t.id)} data-testid={`carnet-move-to-${t.id}`}>
                    <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
                  </button>
                  {childrenOf(t.id).map((k) => (
                    <button key={k.id} style={{ ...s.moveOpt, paddingLeft: 26, ...(e.section === k.id ? s.moveOptActive : {}) }} disabled={e.section === k.id} onClick={() => doMove(e.id, k.id)} data-testid={`carnet-move-to-${k.id}`}>
                      <span style={{ marginRight: 6 }}>{k.icon}</span>{k.label}
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        {showFolder && <button style={{ ...s.folderChip, color: acc, borderColor: `${acc}55` }} onClick={() => { setActive(e.section); setQuery(""); }} data-testid={`carnet-result-folder-${e.id}`}>📁 {catName(e.section)}</button>}
        {e.body && <div style={{ ...s.cardBody, whiteSpace: "pre-wrap" }}>{e.body}</div>}
        {e.video_url && ytId(e.video_url) && (
          <div style={{ marginTop: 10 }} data-testid={`carnet-video-${e.id}`}>
            <YouTubeEmbed url={e.video_url} title={e.title} />
          </div>
        )}
        {e.file_id && isAudio(e.filename) && <CarnetAudio fileId={e.file_id} label={e.filename} testid={`carnet-audio-${e.id}`} />}
        {e.kind === "link" && e.url && (
          <button style={s.preview} onClick={() => openLink(e.url)} data-testid={`carnet-link-${e.id}`}>
            <img src={faviconUrl(dom)} alt="" style={s.previewThumb} onError={(ev) => { ev.target.style.visibility = "hidden"; }} />
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <div style={s.previewLabel}>{linkLabel(dom)}</div>
              <div style={s.previewSub}>{dom}</div>
            </div>
            <IoOpenOutline size={16} style={{ color: acc, flexShrink: 0 }} />
          </button>
        )}
        {e.file_id && !isAudio(e.filename) && (
          isImg(e.filename) ? (
            <CarnetImage fileId={e.file_id} filename={e.filename} testid={`carnet-image-${e.id}`} onOpen={(u, n) => setLightbox({ url: u, name: n })} />
          ) : (
          <button style={s.preview} onClick={() => openCarnetFile(e.file_id)} data-testid={`carnet-file-${e.id}`}>
            <span style={{ ...s.previewThumb, ...s.extBadge, background: acc }}>{fileExt(e.filename)}</span>
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <div style={s.previewLabel}>{e.filename || "Fichier"}</div>
              <div style={s.previewSub}>Document · ouvrir</div>
            </div>
            <IoOpenOutline size={16} style={{ color: acc, flexShrink: 0 }} />
          </button>
          )
        )}
        {!readOnly && e.review_requested && (() => { const ch = reviewChip(e); const unseen = e.sensei_response && e.student_seen === false; return (
          <button style={s.viewReviewBtn} onClick={() => nav(`/carnet/relectures?open=${e.id}`)} data-testid={`carnet-open-review-${e.id}`}>
            <IoChatbubblesOutline size={17} />
            <span style={{ flex: 1, textAlign: "left" }}>Voir la relecture{unseen ? " · nouveau retour !" : ""}</span>
            <span style={{ ...s.reviewChip, background: ch.bg, color: ch.fg }}>{ch.label}</span>
            <IoChevronForward size={15} />
          </button>
        ); })()}
        {readOnly && threadOf(e).length > 0 && (
          <div style={s.senseiBox} data-testid={`carnet-thread-${e.id}`}>
            <div style={s.senseiName}>💬 Échange avec le Sensei</div>
            {threadOf(e).map((m, i) => (
              <div key={m.id || i} style={{ ...s.msg, ...(m.author === "student" ? s.msgStudent : s.msgSensei) }} data-testid={`carnet-msg-${m.author}`}>
                <div style={s.msgAuthor}>{m.author === "student" ? (m.name || "Toi") : `🐱 ${m.name || "Sensei"}`}</div>
                {m.text && <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{m.text}</div>}
                {(m.attachments || []).map((a, x) => a.url && !a.file_id
                  ? <a key={x} href={a.url} target="_blank" rel="noreferrer" style={s.msgAtt}>🔗 {a.url}</a>
                  : <button key={x} style={s.msgAtt} onClick={() => openCarnetFile(a.file_id)}>📎 {a.filename || "Document"}</button>)}
              </div>
            ))}
          </div>
        )}
        {readOnly && e.review_requested && (
          respondTo?.id === e.id ? (
            <div style={{ marginTop: 12 }}>
              <textarea style={{ ...s.input, minHeight: 90 }} value={respondTo.text} onChange={(ev) => setRespondTo({ id: e.id, text: ev.target.value })} placeholder="Ton retour à l'élève…" data-testid={`carnet-respond-input-${e.id}`} />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button style={s.cancel} onClick={() => setRespondTo(null)}>Annuler</button>
                <button style={{ ...s.save, background: RED, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => sendResponse(e.id)} data-testid={`carnet-respond-send-${e.id}`}>Envoyer le retour</button>
              </div>
            </div>
          ) : (
            <button style={s.respondBtn} onClick={() => setRespondTo({ id: e.id, text: e.sensei_response || "" })} data-testid={`carnet-respond-${e.id}`}>✍️ {e.sensei_response ? "Modifier mon retour" : "Répondre à la relecture"}</button>
          )
        )}
      </div>
    );
  };

  const FolderRow = ({ c }) => {
    const kids = childrenOf(c.id);
    const isOpen = expanded[c.id] ?? kids.length > 0;
    const isActive = active === c.id;
    const locked = c.kind === "exercises";
    const over = dragOver === c.id;
    return (
      <div>
        <div style={{ ...s.tab, ...(isActive ? { background: c.color, color: "#fff", borderColor: c.color } : {}), ...(over ? s.tabOver : {}) }}
          data-testid={`carnet-cat-${c.id}`} onClick={() => goFolder(c.id)} {...dropProps(c.id)}>
          {kids.length > 0 ? (
            <span style={s.tabChev} onClick={(e) => { e.stopPropagation(); setExpanded((x) => ({ ...x, [c.id]: !isOpen })); }}>
              {isOpen ? <IoChevronDown size={14} /> : <IoChevronForward size={14} />}
            </span>
          ) : <span style={{ width: 14, flexShrink: 0 }} />}
          <span style={{ ...s.tabIcon, color: isActive ? "#fff" : c.color }}>{c.icon || "📁"}</span>
          <span style={s.tabLabel}>{c.label}</span>
          {!readOnly && !locked && (
            <span style={s.tabActions} onClick={(e) => e.stopPropagation()}>
              <span style={s.tabAct} title="Partager" onClick={() => openShare("folder", c.id, c.label)} data-testid={`carnet-cat-share-${c.id}`}><IoShareSocialOutline size={13} /></span>
              <span style={s.tabAct} title="Modifier" onClick={() => openEditCat(c)} data-testid={`carnet-cat-edit-${c.id}`}><IoCreateOutline size={13} /></span>
              <span style={{ ...s.tabAct, color: isActive ? "#fff" : RED }} title="Supprimer" onClick={() => removeCategory(c)} data-testid={`carnet-cat-del-${c.id}`}><IoTrashOutline size={13} /></span>
            </span>
          )}
        </div>
        {isOpen && kids.map((k) => {
          const kActive = active === k.id;
          const kLocked = k.kind === "exercises";
          const kOver = dragOver === k.id;
          return (
            <div key={k.id} style={{ ...s.tab, ...s.subTab, ...(kActive ? { background: k.color, color: "#fff", borderColor: k.color } : {}), ...(kOver ? s.tabOver : {}) }}
              data-testid={`carnet-cat-${k.id}`} onClick={() => goFolder(k.id)} {...dropProps(k.id)}>
              <span style={{ ...s.tabIcon, color: kActive ? "#fff" : k.color }}>{k.icon || "📁"}</span>
              <span style={s.tabLabel}>{k.label}</span>
              {!readOnly && !kLocked && (
                <span style={s.tabActions} onClick={(e) => e.stopPropagation()}>
                  <span style={s.tabAct} title="Modifier" onClick={() => openEditCat(k)} data-testid={`carnet-cat-edit-${k.id}`}><IoCreateOutline size={13} /></span>
                  <span style={{ ...s.tabAct, color: kActive ? "#fff" : RED }} title="Supprimer" onClick={() => removeCategory(k)} data-testid={`carnet-cat-del-${k.id}`}><IoTrashOutline size={13} /></span>
                </span>
              )}
            </div>
          );
        })}
        {isOpen && !readOnly && (
          <button style={s.addSub} onClick={() => openNewCat(c.id)} data-testid={`carnet-add-sub-${c.id}`}><IoAddOutline size={14} /> Nouveau sous-dossier</button>
        )}
      </div>
    );
  };

  return (
    <DojoShell crumbs={isAdminView ? [{ label: "Membres", to: "/admin/membres" }, { label: data.member?.name || "Élève" }] : [{ label: "Mon Carnet" }]}>
      <div className="carnet-body" onDragOver={onWrapDragOver} onDragLeave={() => setFileDrag(false)} onDrop={handleExternalDrop}>
      {fileDrag && !readOnly && <div style={s.fileDropHint} data-testid="carnet-filedrop-hint"><IoCloudUploadOutline size={18} /> Dépose tes fichiers ici pour les ajouter à « {activeCat?.label || "ce dossier"} »</div>}
      {isAdminView && (
        <button style={s.backBtn} onClick={() => nav("/carnet/eleves")} data-testid="carnet-admin-back"><IoArrowBack size={16} /> Retour aux élèves</button>
      )}

      {/* Bandeau univers */}
      <div style={{ ...s.cover, ...(st.cover ? { backgroundImage: `url(${st.cover})` } : { background: `linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 45%, #000))` }) }} data-testid="carnet-cover">
        <div style={s.coverScrim} />
        <div style={s.coverInner}>
          <div style={s.coverKanji} aria-hidden>帳</div>
          <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
            <div style={s.coverEyebrow}>{isAdminView ? `CARNET DE ${(data.member?.name || "").toUpperCase()}` : "MON DRIVE"}</div>
            <h1 style={s.coverTitle}>{st.universe_name || (isAdminView ? data.member?.name : "Mon univers")}</h1>
          </div>
          {!readOnly && <button style={s.personalizeBtn} onClick={() => setPersonalize(true)} data-testid="carnet-personalize"><IoColorPaletteOutline size={16} /> Personnaliser</button>}
        </div>
      </div>

      {/* Accès complet — côté ADMIN (vue du carnet d'un membre) */}
      {isAdminView && (
        <div style={{ ...s.accessBar, ...(canEditMember ? s.accessBarOn : {}) }} data-testid="carnet-access-admin">
          <span style={{ fontSize: 20 }}>{canEditMember ? "🔓" : "🔒"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={s.accessTitle}>
              {canEditMember ? "Accès complet accordé" : access?.status === "pending" ? "Demande d'accès en attente" : "Accès complet au carnet"}
            </div>
            <div style={s.accessSub}>
              {canEditMember ? "Tu peux ajouter, modifier et supprimer dans ce carnet comme si c'était le tien."
                : access?.status === "pending" ? "Le membre doit accepter ta demande pour que tu puisses modifier son carnet."
                : access?.status === "denied" ? "Ta précédente demande a été refusée. Tu peux en renvoyer une."
                : "Demande au membre l'autorisation de gérer entièrement son carnet."}
            </div>
          </div>
          {canEditMember ? (
            <button style={s.accessDanger} data-testid="carnet-access-revoke-admin" onClick={endAccess}>Mettre fin à l'accès</button>
          ) : access?.status === "pending" ? (
            <span style={s.accessPendingPill}>En attente…</span>
          ) : (
            <button style={s.accessBtn} data-testid="carnet-access-request" onClick={askAccess}>Demander l'accès complet</button>
          )}
        </div>
      )}

      {/* Accès complet — côté MEMBRE (demandes/accords me concernant) */}
      {!isAdminView && accessReqs.length > 0 && (
        <div style={s.accessMemberWrap} data-testid="carnet-access-member">
          {accessReqs.map((a) => (
            <div key={a.id} style={{ ...s.accessBar, ...(a.status === "granted" ? s.accessBarOn : {}) }} data-testid={`carnet-access-req-${a.id}`}>
              <span style={{ fontSize: 20 }}>{a.status === "granted" ? "🔓" : "🙋"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={s.accessTitle}>
                  {a.status === "granted" ? `${a.admin_name} a accès à ton carnet` : `${a.admin_name} demande l'accès complet à ton carnet`}
                </div>
                <div style={s.accessSub}>
                  {a.status === "granted" ? "Il peut ajouter, modifier et supprimer dans ton carnet. Tu peux retirer cet accès à tout moment."
                    : "En acceptant, il pourra ajouter et organiser des éléments dans ton carnet comme toi."}
                </div>
              </div>
              {a.status === "granted" ? (
                <button style={s.accessDanger} data-testid={`carnet-access-revoke-${a.id}`} onClick={() => memberRevoke(a.id)}>Retirer l'accès</button>
              ) : (
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button style={s.accessBtn} data-testid={`carnet-access-accept-${a.id}`} onClick={() => answerAccess(a.id, true)}>Accepter</button>
                  <button style={s.accessGhost} data-testid={`carnet-access-deny-${a.id}`} onClick={() => answerAccess(a.id, false)}>Refuser</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}


      {/* Rappel : le Sensei a répondu */}
      {respondedUnseen.length > 0 && (
        <div style={s.reminder} data-testid="carnet-review-reminder">
          <span style={{ fontSize: 22 }}>🐱</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 900, color: "var(--text-primary)", fontSize: 14.5 }}>Le Sensei t'a répondu !</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{respondedUnseen.length} retour(s) sur tes demandes de relecture t'attendent.</div>
          </div>
          <button style={s.reminderBtn} data-testid="carnet-review-goto" onClick={() => nav("/carnet/relectures")}>Voir</button>
          <button style={s.reminderX} data-testid="carnet-review-seen" onClick={markSeen} title="Marquer comme lu"><IoClose size={18} /></button>
        </div>
      )}

      {/* Intro Nerodus (éditable via Paramètres › Nerodus) */}
      {/* Barre d'action : ouvrir le tiroir + titre + recherche + ajouter */}
      <div style={s.bar} className="carnet-bar">
        <div style={s.barTop} className="carnet-bartop">
          <button style={s.drawerBtn} onClick={() => setDrawerOpen(true)} data-testid="carnet-open-drawer"><IoReorderThreeOutline size={20} /> Dossiers</button>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }} className="carnet-actions">
            {!readOnly && <button style={s.reviewsBtn} onClick={() => nav("/carnet/relectures")} data-testid="carnet-my-reviews"><IoChatbubblesOutline size={17} /> Mes relectures</button>}
            {!readOnly && <button style={{ ...s.reviewsBtn, position: "relative" }} onClick={() => nav("/carnet/partages")} data-testid="carnet-partages"><IoShareSocialOutline size={17} /> Partages{shareUnseen > 0 && <span style={s.badgeDot} data-testid="carnet-partages-badge">{shareUnseen}</span>}</button>}
            {!readOnly && activeCat && <button style={{ ...s.newBtn, background: activeAccent }} onClick={openNew} data-testid="carnet-new-entry"><IoAddOutline size={17} /> Ajouter</button>}
          </div>
        </div>
        <h2 style={s.contentTitle} className="carnet-title"><span style={{ marginRight: 10, color: activeAccent }}>{activeCat?.icon || "📁"}</span>{activeCat?.label || "Dossier"}</h2>
        <div style={s.searchWrap} className="carnet-search">
          <IoSearchOutline size={17} style={{ color: "var(--text-secondary)", flexShrink: 0 }} />
          <input style={s.searchInput} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher dans tout le Drive…" data-testid="carnet-search" />
          {query && <button style={s.searchClear} onClick={() => setQuery("")} data-testid="carnet-search-clear"><IoClose size={16} /></button>}
        </div>
      </div>

      {/* Contenu pleine largeur */}
      <div style={s.content}>
        {searchResults !== null ? (
          <>
            <div style={s.groupLabel} data-testid="carnet-search-count">{searchResults.length} résultat{searchResults.length > 1 ? "s" : ""} pour « {query} »</div>
            {searchResults.length === 0 ? (
              <div style={s.empty} data-testid="carnet-search-empty">Aucune note, fichier ou lien ne correspond.</div>
            ) : (
              <div style={gridStyle}>{searchResults.map((e) => renderEntry(e, true))}</div>
            )}
          </>
        ) : (
          <>
            {exercises.length > 0 && (
              <>
                <div style={s.groupLabel}>Exercices rendus</div>
                <div style={gridStyle}>
                  {exercises.map((ex) => (
                    <div key={ex.id} className="ct-card" style={{ ...s.card, ...cardExtra }} data-testid={`carnet-exercise-${ex.lesson_id}`}>
                      <div style={s.cardHead}><span style={{ ...s.pill, background: `${activeAccent}18`, color: activeAccent }}><IoCheckmarkCircle size={13} /> {ex.lesson_title}</span></div>
                      {ex.content && <div style={s.cardBody} dangerouslySetInnerHTML={{ __html: ex.content }} />}
                      {ex.file_id && <button style={s.fileChip} onClick={() => openCarnetFile(ex.file_id)} data-testid={`carnet-exfile-${ex.lesson_id}`}><IoDocumentAttachOutline size={16} /> {ex.filename || "Fichier rendu"}</button>}
                    </div>
                  ))}
                </div>
              </>
            )}
            {exercises.length > 0 && entries.length > 0 && <div style={s.groupLabel}>Mes notes, fichiers & liens</div>}
            {entries.length === 0 && exercises.length === 0 && (
              <div style={s.empty} data-testid="carnet-empty">Ce dossier est vide. {readOnly ? "" : "Ajoute une note, un fichier ou un lien."}</div>
            )}
            <div style={gridStyle}>{entries.map((e) => renderEntry(e, false, !readOnly))}</div>
          </>
        )}
      </div>

      {/* Tiroir latéral des dossiers */}
      {drawerOpen && (
        <div style={{ ...s.drawerOverlay, ...(dragId ? { background: "transparent", backdropFilter: "none", pointerEvents: "none" } : {}) }} onClick={() => setDrawerOpen(false)} data-testid="carnet-drawer">
          <div style={{ ...s.drawer, ...(dragId ? { pointerEvents: "auto" } : {}) }} onClick={(e) => e.stopPropagation()}>
            <div style={s.drawerHead}>
              <span style={s.drawerTitle}><IoFolderOpenOutline size={20} /> Mes dossiers</span>
              <button style={s.nerodusX} onClick={() => setDrawerOpen(false)} data-testid="carnet-drawer-close"><IoClose size={22} /></button>
            </div>
            {!readOnly && dragId && <div style={s.dragHint}>Dépose sur un dossier pour y déplacer l'entrée — ou sur une autre carte pour la réordonner</div>}
            <div style={s.drawerList}>
              {tops.map((c) => <FolderRow key={c.id} c={c} />)}
            </div>
            {!readOnly && <button style={s.addCat} onClick={() => openNewCat(null)} data-testid="carnet-add-category"><IoAddOutline size={16} /> Nouveau dossier</button>}
          </div>
        </div>
      )}

      {/* Modale entrée */}
      {editor && (
        <div style={s.overlay} onClick={() => setEditor(null)}>
          <div style={s.modal} onClick={(ev) => ev.stopPropagation()} data-testid="carnet-entry-modal">
            <div style={s.modalHead}><span style={s.modalTitle}>{editor.id ? "Modifier l'entrée" : "Nouvelle entrée"}</span><button style={s.modalX} onClick={() => setEditor(null)}><IoClose size={20} /></button></div>
            <div style={s.modalBody}>
              <label style={s.lbl}>Type</label>
              <div style={s.typeRow}>
                {[{ k: "note", ic: <IoCreateOutline size={15} />, t: "Note" }, { k: "file", ic: <IoDocumentAttachOutline size={15} />, t: "Fichier" }, { k: "link", ic: <IoLinkOutline size={15} />, t: "Lien" }, { k: "audio", ic: <IoMusicalNotesOutline size={15} />, t: "Audio" }, { k: "video", ic: <IoLogoYoutube size={15} />, t: "Vidéo" }].map((o) => (
                  <button key={o.k} data-testid={`carnet-entry-type-${o.k}`} onClick={() => setEditor({ ...editor, kind: o.k })}
                    style={{ ...s.typeBtn, ...(editor.kind === o.k ? { background: accent, color: "#fff", border: `1.5px solid ${accent}` } : {}) }}>{o.ic} {o.t}</button>
                ))}
              </div>
              <label style={s.lbl}>Dossier</label>
              <select style={s.input} value={editor.section} onChange={(e) => setEditor({ ...editor, section: e.target.value })} data-testid="carnet-entry-section">
                {tops.flatMap((t) => [
                  <option key={t.id} value={t.id}>{t.label}</option>,
                  ...childrenOf(t.id).map((k) => <option key={k.id} value={k.id}>&nbsp;&nbsp;↳ {k.label}</option>),
                ])}
              </select>
              <label style={s.lbl}>Titre</label>
              <input style={s.input} value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} placeholder="Titre de l'entrée" data-testid="carnet-entry-title" />
              {editor.kind === "link" && (
                <><label style={s.lbl}>Lien (Canva, Google Docs, Notion…)</label>
                <input style={s.input} value={editor.url} onChange={(e) => setEditor({ ...editor, url: e.target.value })} placeholder="https://…" data-testid="carnet-entry-url" /></>
              )}
              {editor.kind === "video" && (
                <><label style={s.lbl}>Lien YouTube</label>
                <input style={s.input} value={editor.video_url || ""} onChange={(e) => setEditor({ ...editor, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid="carnet-entry-video" />
                {editor.video_url && ytId(editor.video_url) && (
                  <div style={{ ...s.videoWrap, marginTop: 10 }}><iframe style={s.videoFrame} src={`https://www.youtube.com/embed/${ytId(editor.video_url)}`} title="preview" frameBorder="0" allowFullScreen /></div>
                )}</>
              )}
              {(editor.kind === "file" || editor.kind === "audio") && (
                <div style={s.fileRow}>
                  <button style={s.uploadBtn} onClick={() => fileRef.current?.click()} disabled={busy} data-testid="carnet-entry-upload"><IoCloudUploadOutline size={16} /> {editor.file_id ? "Remplacer le fichier" : (editor.kind === "audio" ? "Joindre un audio (MP3, WAV…)" : "Joindre un fichier (PDF, texte, image…)")}</button>
                  {editor.filename && <span style={s.fileName}>{editor.filename}</span>}
                  <input ref={fileRef} type="file" accept={editor.kind === "audio" ? "audio/*,.mp3,.wav,.m4a,.ogg,.aac" : ".pdf,.doc,.docx,.txt,.rtf,.odt,.md,image/*"} style={{ display: "none" }} onChange={onFile} />
                </div>
              )}
              {editor.kind === "audio" && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginBottom: 6 }}>…ou enregistre un mémo vocal :</div>
                  <VoiceRecorder testid="carnet-voice" onRecorded={async (file) => {
                    if (!file) { setEditor((cur) => ({ ...cur, file_id: null, filename: null })); return; }
                    setBusy(true);
                    try { const r = await uploadCarnetFile(file); setEditor((cur) => ({ ...cur, file_id: r.file_id, filename: r.filename })); toast.success("Vocal enregistré"); }
                    catch { toast.error("Upload du vocal impossible"); } finally { setBusy(false); }
                  }} />
                </div>
              )}
              <label style={s.lbl}>{editor.kind === "note" ? "Texte" : "Note (optionnel)"}</label>
              <textarea style={{ ...s.input, minHeight: 120, resize: "vertical", fontFamily: "inherit" }} value={editor.body} onChange={(e) => setEditor({ ...editor, body: e.target.value })} placeholder="Écris ici…" data-testid="carnet-entry-body" />
              <label style={s.check}><input type="checkbox" checked={editor.review_requested} onChange={(e) => setEditor({ ...editor, review_requested: e.target.checked })} style={{ accentColor: accent }} data-testid="carnet-entry-review" /> Demander une relecture au Sensei</label>
            </div>
            <div style={s.modalActions}>
              <button style={s.cancel} onClick={() => setEditor(null)}>Annuler</button>
              <button style={{ ...s.save, background: accent, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={saveEntry} data-testid="carnet-entry-save">{busy ? "…" : "Enregistrer"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale dossier */}
      {catEditor && (
        <div style={s.overlay} onClick={() => setCatEditor(null)}>
          <div style={s.modal} onClick={(ev) => ev.stopPropagation()} data-testid="carnet-cat-modal">
            <div style={s.modalHead}><span style={s.modalTitle}>{catEditor.mode === "edit" ? "Modifier le dossier" : catEditor.data.parent_id ? "Nouveau sous-dossier" : "Nouveau dossier"}</span><button style={s.modalX} onClick={() => setCatEditor(null)}><IoClose size={20} /></button></div>
            <div style={s.modalBody}>
              <label style={s.lbl}>Nom du dossier</label>
              <input style={s.input} value={catEditor.data.label} autoFocus onChange={(e) => setCatEditor({ ...catEditor, data: { ...catEditor.data, label: e.target.value } })} placeholder="Ex: Personnages, Brouillons…" data-testid="carnet-cat-label" />
              <label style={s.lbl}>Icône</label>
              <div style={s.emojiGrid}>
                {EMOJIS.map((em) => (
                  <button key={em} onClick={() => setCatEditor({ ...catEditor, data: { ...catEditor.data, icon: em } })} data-testid={`carnet-cat-emoji-${em}`}
                    style={{ ...s.emojiBtn, ...(catEditor.data.icon === em ? { borderColor: catEditor.data.color, background: `${catEditor.data.color}18` } : {}) }}>{em}</button>
                ))}
                <input style={{ ...s.input, width: 60, textAlign: "center", padding: "8px" }} value={catEditor.data.icon || ""} onChange={(e) => setCatEditor({ ...catEditor, data: { ...catEditor.data, icon: e.target.value.slice(0, 2) } })} placeholder="✎" data-testid="carnet-cat-emoji-custom" />
              </div>
              <label style={s.lbl}>Couleur</label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {ACCENTS.map((c) => (
                  <button key={c} onClick={() => setCatEditor({ ...catEditor, data: { ...catEditor.data, color: c } })} data-testid={`carnet-cat-color-${c}`}
                    style={{ width: 34, height: 34, borderRadius: "50%", background: c, border: catEditor.data.color === c ? "3px solid var(--text-primary)" : "2px solid var(--border-c)", cursor: "pointer" }} />
                ))}
              </div>
            </div>
            <div style={s.modalActions}>
              <button style={s.cancel} onClick={() => setCatEditor(null)}>Annuler</button>
              <button style={{ ...s.save, background: catEditor.data.color, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={saveCat} data-testid="carnet-cat-save">{busy ? "…" : "Enregistrer"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de partage */}
      {shareFor && (
        <div style={s.overlay} onClick={() => setShareFor(null)}>
          <div style={s.modal} onClick={(ev) => ev.stopPropagation()} data-testid="carnet-share-modal">
            <div style={s.modalHead}><span style={s.modalTitle}>Partager « {shareFor.title} »</span><button style={s.modalX} onClick={() => setShareFor(null)}><IoClose size={20} /></button></div>
            <div style={s.modalBody}>
              {shareResult ? (
                <>
                  <label style={s.lbl}>Lien de partage créé</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input style={s.input} readOnly value={shareResult} data-testid="carnet-share-link" onFocus={(e) => e.target.select()} />
                    <button style={{ ...s.save, background: accent }} onClick={() => { navigator.clipboard?.writeText(shareResult); toast.success("Lien copié"); }} data-testid="carnet-share-copy"><IoCopyOutline size={16} /></button>
                  </div>
                  <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: "6px 0 0" }}>Toute personne connectée disposant de ce lien pourra accéder au contenu selon le niveau choisi.</p>
                </>
              ) : (
                <>
                  <label style={s.lbl}>Partager avec</label>
                  <div style={s.typeRow}>
                    <button style={{ ...s.typeBtn, ...(shareCfg.mode === "member" ? { background: accent, color: "#fff", border: `1.5px solid ${accent}` } : {}) }} onClick={() => setShareCfg({ ...shareCfg, mode: "member" })} data-testid="carnet-share-mode-member">Un membre</button>
                    <button style={{ ...s.typeBtn, ...(shareCfg.mode === "link" ? { background: accent, color: "#fff", border: `1.5px solid ${accent}` } : {}) }} onClick={() => setShareCfg({ ...shareCfg, mode: "link" })} data-testid="carnet-share-mode-link">Un lien</button>
                  </div>
                  {shareCfg.mode === "member" && (
                    <>
                      <label style={s.lbl}>Membre du Dōjō</label>
                      <select style={s.input} value={shareCfg.member_id} onChange={(e) => setShareCfg({ ...shareCfg, member_id: e.target.value })} data-testid="carnet-share-member">
                        <option value="">Choisir un membre…</option>
                        {members.filter((m) => m.id !== user?.id).map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
                      </select>
                    </>
                  )}
                  <label style={s.lbl}>Niveau d'accès</label>
                  <select style={s.input} value={shareCfg.permission} onChange={(e) => setShareCfg({ ...shareCfg, permission: e.target.value })} data-testid="carnet-share-permission">
                    <option value="view">Lecture seule</option>
                    <option value="comment">Lecture + commentaires</option>
                    <option value="edit">Modification</option>
                  </select>
                </>
              )}
            </div>
            <div style={s.modalActions}>
              <button style={s.cancel} onClick={() => setShareFor(null)}>Fermer</button>
              {!shareResult && <button style={{ ...s.save, background: accent, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={doShare} data-testid="carnet-share-confirm">Partager</button>}
            </div>
          </div>
        </div>
      )}

      {/* Modale personnalisation */}
      {personalize && (
        <div style={s.overlay} onClick={() => setPersonalize(false)}>
          <div style={s.modal} onClick={(ev) => ev.stopPropagation()} data-testid="carnet-personalize-modal">
            <div style={s.modalHead}><span style={s.modalTitle}>Personnaliser mon carnet</span><button style={s.modalX} onClick={() => setPersonalize(false)}><IoClose size={20} /></button></div>
            <div style={s.modalBody}>
              <label style={s.lbl}>Disposition du carnet</label>
              <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: "0 0 4px" }}>Change la façon dont tes entrées s'affichent.</p>
              <div style={s.themeGrid} data-testid="carnet-layout-grid">
                {CARNET_LAYOUTS.map((l) => {
                  const on = (st.layout || "grid") === l.key;
                  return (
                    <button key={l.key} data-testid={`carnet-layout-${l.key}`} onClick={() => saveSettings({ layout: l.key })}
                      style={{ ...s.themeCard, ...(on ? { borderColor: "var(--brand)", boxShadow: "0 0 0 2px var(--brand)" } : {}) }}>
                      <span style={s.layoutIcon}>{l.icon}</span>
                      <span style={s.themeName}>{l.name}{on ? " ✓" : ""}</span>
                      <span style={s.themeDesc}>{l.desc}</span>
                    </button>
                  );
                })}
              </div>
              <label style={s.lbl}>Thème — ambiance de toute l'interface</label>
              <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: "0 0 4px" }}>Change les couleurs et la typo de toute la plateforme (comme dans les Paramètres).</p>
              <div style={s.themeGrid} data-testid="carnet-theme-grid">
                {THEMES.filter((t) => FEATURED_THEME_KEYS.includes(t.key)).map((t) => {
                  const on = theme === t.key;
                  return (
                    <button key={t.key} data-testid={`carnet-theme-${t.key}`} onClick={() => { setTheme(t.key); toast.success(`Thème ${t.name}`); }}
                      style={{ ...s.themeCard, ...(on ? { borderColor: "var(--brand)", boxShadow: "0 0 0 2px var(--brand)" } : {}) }}>
                      <span style={s.themeSwatch}>{t.swatch.map((c, i) => <span key={i} style={{ background: c, flex: 1 }} />)}</span>
                      <span style={s.themeName}>{t.name}{on ? " ✓" : ""}</span>
                      <span style={s.themeDesc}>{t.tagline}</span>
                    </button>
                  );
                })}
              </div>
              <label style={s.lbl}>Nom de ton univers</label>
              <input style={s.input} value={st.universe_name || ""} onChange={(e) => setData({ ...data, settings: { ...st, universe_name: e.target.value } })} placeholder="Ex: Les Terres d'Aube" data-testid="carnet-universe-name" />
              <label style={s.lbl}>Image de couverture</label>
              {st.cover && <div style={{ ...s.coverPreview, backgroundImage: `url(${st.cover})` }} data-testid="carnet-cover-preview" />}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <button style={s.uploadBtn} onClick={() => coverRef.current?.click()} data-testid="carnet-cover-upload"><IoCloudUploadOutline size={16} /> Uploader une image</button>
                {st.cover && <button style={s.cancel} onClick={() => setData({ ...data, settings: { ...st, cover: "" } })} data-testid="carnet-cover-remove">Retirer</button>}
                <input ref={coverRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onCoverFile} />
              </div>
              <label style={s.lbl}>…ou coller une URL d'image</label>
              <input style={s.input} value={(st.cover || "").startsWith("data:") ? "" : (st.cover || "")} onChange={(e) => setData({ ...data, settings: { ...st, cover: e.target.value } })} placeholder="https://…" data-testid="carnet-cover-url" />
              <label style={s.lbl}>Couleur d'accent</label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {ACCENTS.map((c) => (
                  <button key={c} onClick={() => setData({ ...data, settings: { ...st, accent: c } })} data-testid={`carnet-accent-${c}`}
                    style={{ width: 34, height: 34, borderRadius: "50%", background: c, border: st.accent === c ? "3px solid var(--text-primary)" : "2px solid var(--border-c)", cursor: "pointer" }} />
                ))}
              </div>
            </div>
            <div style={s.modalActions}>
              <button style={s.cancel} onClick={() => { setPersonalize(false); load(); }}>Fermer</button>
              <button style={{ ...s.save, background: accent }} onClick={async () => { await saveSettings({ universe_name: st.universe_name, cover: st.cover, accent: st.accent, layout: st.layout || "grid" }); setPersonalize(false); toast.success("Carnet personnalisé"); }} data-testid="carnet-personalize-save">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
      </div>
      {lightbox && (
        <div style={s.lightbox} data-testid="carnet-lightbox" onClick={() => setLightbox(null)}>
          <button style={s.lightboxClose} data-testid="carnet-lightbox-close" onClick={() => setLightbox(null)}><IoClose size={22} /></button>
          <img src={lightbox.url} alt={lightbox.name || "Image"} style={s.lightboxImg} onClick={(e) => e.stopPropagation()} />
          {lightbox.name && <div style={s.lightboxCap}>{lightbox.name}</div>}
        </div>
      )}

    </DojoShell>
  );
}

const s = {
  backBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13, cursor: "pointer", marginBottom: 16 },
  cover: { position: "relative", overflow: "hidden", borderRadius: 24, padding: "32px 28px", marginBottom: 18, backgroundSize: "cover", backgroundPosition: "center", minHeight: 150 },
  coverScrim: { position: "absolute", inset: 0, background: "linear-gradient(120deg, rgba(0,0,0,0.55), rgba(0,0,0,0.2))" },
  coverInner: { position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  coverKanji: { fontSize: 72, fontWeight: 800, color: "rgba(255,255,255,0.18)", lineHeight: 1, fontFamily: "var(--font-display)" },
  coverEyebrow: { color: "rgba(255,255,255,0.85)", fontSize: 12, fontWeight: 800, letterSpacing: 2 },
  coverTitle: { color: "#fff", fontSize: 34, fontWeight: 900, margin: "4px 0 6px", fontFamily: "var(--font-display)" },
  accessBar: { display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 16, marginBottom: 16, border: "1px solid color-mix(in srgb, var(--brand) 35%, var(--border-c))", background: "color-mix(in srgb, var(--brand) 8%, var(--bg-secondary))", boxShadow: "var(--surface-shadow)", flexWrap: "wrap" },
  accessBarOn: { border: "1px solid color-mix(in srgb, #00c27a 55%, var(--border-c))", background: "color-mix(in srgb, #00c27a 10%, var(--bg-secondary))" },
  accessMemberWrap: { display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 },
  accessTitle: { fontSize: 14.5, fontWeight: 900, color: "var(--text-primary)" },
  accessSub: { fontSize: 12.5, color: "var(--text-secondary)", marginTop: 2, lineHeight: 1.45 },
  accessBtn: { flexShrink: 0, padding: "10px 16px", borderRadius: 10, border: "none", background: "var(--brand)", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" },
  accessGhost: { flexShrink: 0, padding: "10px 16px", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 800, fontSize: 13, cursor: "pointer" },
  accessDanger: { flexShrink: 0, padding: "10px 16px", borderRadius: 10, border: "1px solid color-mix(in srgb, #e11d2a 45%, var(--border-c))", background: "transparent", color: "#e11d2a", fontWeight: 800, fontSize: 13, cursor: "pointer" },
  accessPendingPill: { flexShrink: 0, padding: "8px 14px", borderRadius: 999, background: "var(--bg-tertiary)", color: "var(--text-secondary)", fontWeight: 800, fontSize: 12.5 },
  coverSub: { color: "rgba(255,255,255,0.9)", fontSize: 14, margin: 0 },
  coverPreview: { width: "100%", height: 110, borderRadius: 12, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--border-c)" },
  personalizeBtn: { position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: 999, border: "none", background: "rgba(255,255,255,0.95)", color: "#111", fontWeight: 800, fontSize: 13, cursor: "pointer" },
  reminder: { display: "flex", gap: 12, alignItems: "center", padding: "14px 16px", borderRadius: 16, border: "1.5px solid color-mix(in srgb, var(--brand) 45%, transparent)", background: "color-mix(in srgb, var(--brand) 9%, var(--bg-secondary))", marginBottom: 18 },
  reminderBtn: { padding: "9px 18px", borderRadius: 999, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer", flexShrink: 0 },
  reminderX: { width: 34, height: 34, borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  nerodus: { display: "flex", gap: 14, alignItems: "flex-start", padding: "16px 18px", borderRadius: 18, border: "1.5px solid", background: "var(--bg-secondary)", marginBottom: 20, boxShadow: "var(--surface-shadow)" },
  nerodusAvatar: { fontSize: 30, width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "var(--bg-tertiary)", flexShrink: 0 },
  nerodusName: { fontWeight: 900, color: "var(--text-primary)", fontSize: 14, marginBottom: 3 },
  nerodusText: { color: "var(--text-secondary)", fontSize: 13.5, lineHeight: 1.55, margin: "0 0 4px" },
  nerodusX: { border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer", flexShrink: 0 },
  bar: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 18 },
  barTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%" },
  drawerBtn: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: 12, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 800, fontSize: 14, cursor: "pointer", flexShrink: 0 },
  contentTitle: { fontSize: 24, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)", margin: 0, display: "flex", alignItems: "center", minWidth: 0, wordBreak: "break-word", lineHeight: 1.2 },
  searchWrap: { display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", width: "100%", maxWidth: 420 },
  searchInput: { border: "none", outline: "none", background: "transparent", color: "var(--text-primary)", fontSize: 14, flex: 1, width: "100%", minWidth: 0 },
  searchClear: { border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer", display: "flex", flexShrink: 0 },
  folderChip: { display: "inline-flex", alignItems: "center", gap: 5, alignSelf: "flex-start", marginBottom: 8, padding: "4px 11px", borderRadius: 999, border: "1px solid", background: "transparent", fontWeight: 700, fontSize: 12, cursor: "pointer" },
  newBtn: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 12, border: "none", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", flexShrink: 0 },
  content: {},
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 },
  groupLabel: { fontSize: 12, fontWeight: 800, letterSpacing: 0.5, color: "var(--text-secondary)", textTransform: "uppercase", margin: "6px 0 12px" },
  empty: { padding: "40px 20px", textAlign: "center", color: "var(--text-secondary)", border: "1px dashed var(--border-c)", borderRadius: 16, fontSize: 14 },
  card: { padding: "16px 18px", borderRadius: "var(--ct-radius, 16px)", border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)", cursor: "grab" },
  cardDropOver: { outline: `2px dashed ${RED}`, outlineOffset: 2, boxShadow: `0 -4px 0 -1px ${RED}, var(--surface-shadow)` },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 },
  entryTitle: { fontWeight: 800, color: "var(--text-primary)", fontSize: 15.5, display: "flex", alignItems: "center", minWidth: 0 },
  pill: { display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" },
  cardBody: { color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.55 },
  iconMini: { width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", cursor: "pointer", flexShrink: 0 },
  movePanel: { marginTop: 10, padding: 10, borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-primary)" },
  moveTitle: { fontSize: 12, fontWeight: 800, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 },
  moveList: { display: "flex", flexDirection: "column", gap: 2, maxHeight: 220, overflowY: "auto" },
  moveOpt: { display: "flex", alignItems: "center", textAlign: "left", padding: "8px 10px", borderRadius: 8, border: "none", background: "transparent", color: "var(--text-primary)", fontSize: 13.5, cursor: "pointer", width: "100%" },
  moveOptActive: { background: "var(--bg-tertiary)", opacity: 0.5, cursor: "default" },
  preview: { display: "flex", alignItems: "center", gap: 12, marginTop: 10, padding: "10px 12px", borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", cursor: "pointer", width: "100%" },
  lightbox: { position: "fixed", inset: 0, zIndex: 6000, background: "rgba(0,0,0,0.88)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, cursor: "zoom-out" },
  lightboxImg: { maxWidth: "92vw", maxHeight: "82vh", objectFit: "contain", borderRadius: 10, boxShadow: "0 20px 60px rgba(0,0,0,0.6)" },
  lightboxClose: { position: "absolute", top: 18, right: 18, width: 44, height: 44, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  lightboxCap: { marginTop: 14, color: "rgba(255,255,255,0.9)", fontSize: 14, fontWeight: 600 },
  previewThumb: { width: 40, height: 40, borderRadius: 9, flexShrink: 0, objectFit: "cover", background: "var(--bg-secondary)", border: "1px solid var(--border-c)" },
  extBadge: { display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 11, border: "none" },
  previewLabel: { fontWeight: 800, fontSize: 13.5, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  previewSub: { fontSize: 12, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  senseiBox: { marginTop: 12, padding: "12px 14px", borderRadius: 12, border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)", background: "color-mix(in srgb, var(--brand) 8%, transparent)", color: "var(--text-primary)", fontSize: 13.5, lineHeight: 1.55 },
  senseiName: { fontWeight: 800, color: RED, fontSize: 13, marginBottom: 8 },
  msg: { padding: "8px 11px", borderRadius: 10, marginBottom: 7, maxWidth: "92%" },
  msgSensei: { background: "color-mix(in srgb, var(--brand) 14%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 25%, transparent)", marginRight: "auto" },
  msgStudent: { background: "var(--bg-tertiary)", border: "1px solid var(--border-c)", marginLeft: "auto" },
  msgAuthor: { fontWeight: 800, fontSize: 11.5, opacity: 0.75, marginBottom: 3 },
  msgAtt: { display: "block", marginTop: 5, color: RED, fontWeight: 700, fontSize: 12.5, background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", wordBreak: "break-all", textAlign: "left" },
  respondBtn: { marginTop: 12, padding: "10px 16px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "transparent", color: RED, fontWeight: 800, fontSize: 13, cursor: "pointer" },
  viewReviewBtn: { display: "flex", alignItems: "center", gap: 8, width: "100%", marginTop: 12, padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${RED}`, background: "color-mix(in srgb, var(--brand) 6%, transparent)", color: RED, fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  reviewChip: { padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" },
  reviewsBtn: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: 12, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 800, fontSize: 14, cursor: "pointer", flexShrink: 0 },
  videoWrap: { position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", marginTop: 10, background: "#000" },
  videoFrame: { position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" },
  replyAttList: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 },
  replyAttChip: { display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 999, background: "var(--bg-tertiary)", border: "1px solid var(--border-c)", fontSize: 12, fontWeight: 700, color: "var(--text-primary)" },
  attX: { border: "none", background: "transparent", color: RED, cursor: "pointer", fontSize: 16, fontWeight: 900, lineHeight: 1 },
  replyTools: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 10 },
  replyTool: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 12.5, cursor: "pointer" },
  replyLinkRow: { display: "flex", gap: 6, alignItems: "center", flex: 1, minWidth: 180 },
  replyLinkInput: { flex: 1, padding: "8px 11px", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", fontSize: 13, minWidth: 110 },
  // Drawer
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)", zIndex: 5000, display: "flex", overscrollBehavior: "contain" },
  drawer: { width: "min(340px, 90vw)", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--bg-secondary)", borderRight: "1px solid var(--border-c)", padding: "20px 18px", boxShadow: "8px 0 40px rgba(0,0,0,0.3)", animation: "carnetSlide .25s ease" },
  drawerHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexShrink: 0 },
  drawerTitle: { fontWeight: 900, fontSize: 18, color: "var(--text-primary)", fontFamily: "var(--font-display)", display: "flex", alignItems: "center", gap: 8 },
  drawerList: { display: "flex", flexDirection: "column", gap: 4, flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-y", paddingBottom: 8 },
  dragHint: { fontSize: 12, fontWeight: 700, color: RED, background: "color-mix(in srgb, var(--brand) 10%, transparent)", padding: "8px 12px", borderRadius: 10, marginBottom: 10 },
  tab: { display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px", borderRadius: 11, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13.5, cursor: "pointer", textAlign: "left" },
  tabOver: { outline: `2px dashed ${RED}`, outlineOffset: 1, background: "color-mix(in srgb, var(--brand) 10%, transparent)" },
  subTab: { marginLeft: 16, marginTop: 4, background: "var(--bg-tertiary)" },
  tabChev: { display: "flex", flexShrink: 0, opacity: 0.7, marginTop: 2 },
  tabIcon: { fontSize: 16, width: 20, textAlign: "center", flexShrink: 0, lineHeight: 1.3 },
  tabLabel: { flex: 1, lineHeight: 1.3, wordBreak: "break-word" },
  tabActions: { display: "flex", gap: 4, alignItems: "center", flexShrink: 0 },
  tabAct: { width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 6, opacity: 0.85 },
  addSub: { display: "flex", alignItems: "center", gap: 6, marginLeft: 16, marginTop: 4, padding: "8px 11px", borderRadius: 10, border: "1px dashed var(--border-c)", background: "transparent", color: "var(--text-secondary)", fontWeight: 700, fontSize: 12.5, cursor: "pointer", width: "calc(100% - 16px)", textAlign: "left" },
  addCat: { display: "flex", alignItems: "center", gap: 6, padding: "11px 13px", borderRadius: 12, border: "1px dashed var(--border-c)", background: "transparent", color: "var(--text-primary)", fontWeight: 800, fontSize: 13, cursor: "pointer", marginTop: 12, width: "100%", flexShrink: 0 },
  // Modales
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", zIndex: 6000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  modal: { width: "100%", maxWidth: 560, maxHeight: "88vh", overflow: "auto", background: "var(--bg-secondary)", borderRadius: 20, border: "1px solid var(--border-c)" },
  modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "1px solid var(--border-c)" },
  modalTitle: { fontWeight: 900, fontSize: 17, color: "var(--text-primary)" },
  modalX: { border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer" },
  modalBody: { padding: "20px 22px", display: "flex", flexDirection: "column", gap: 10 },
  lbl: { fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginTop: 4 },
  input: { padding: "11px 13px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, color: "var(--text-primary)", background: "var(--bg-tertiary)", outline: "none", width: "100%", boxSizing: "border-box" },
  typeRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  typeBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 10, border: "1.5px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13.5, cursor: "pointer" },
  emojiGrid: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
  emojiBtn: { width: 40, height: 40, fontSize: 20, borderRadius: 10, border: "2px solid var(--border-c)", background: "var(--bg-tertiary)", cursor: "pointer" },
  fileRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 },
  uploadBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, border: "1px dashed var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  fileName: { fontSize: 12.5, color: "var(--text-secondary)" },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "var(--text-primary)", marginTop: 6, cursor: "pointer" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 22px", borderTop: "1px solid var(--border-c)" },
  cancel: { padding: "11px 18px", borderRadius: 10, border: "1px solid var(--border-c)", background: "transparent", color: "var(--text-primary)", fontWeight: 700, fontSize: 14, cursor: "pointer" },
  save: { padding: "11px 22px", borderRadius: 10, border: "none", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" },
  themeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginBottom: 6 },
  themeCard: { display: "flex", flexDirection: "column", gap: 6, padding: 10, borderRadius: 12, border: "1.5px solid var(--border-c)", background: "var(--bg-tertiary)", cursor: "pointer", textAlign: "left" },
  themeSwatch: { display: "flex", height: 26, borderRadius: 7, overflow: "hidden", border: "1px solid var(--border-c)" },
  themeName: { fontSize: 13.5, fontWeight: 800, color: "var(--text-primary)" },
  themeDesc: { fontSize: 11.5, color: "var(--text-secondary)", lineHeight: 1.35 },
  layoutIcon: { fontSize: 26, lineHeight: 1, color: "var(--brand)" },
  fileDropHint: { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "16px", marginBottom: 14, borderRadius: 16, border: "2px dashed var(--brand)", background: "color-mix(in srgb, var(--brand) 8%, var(--bg-secondary))", color: "var(--text-primary)", fontWeight: 700, fontSize: 14 },
  nerodusCta: { display: "inline-flex", alignItems: "center", gap: 7, marginTop: 8, padding: "9px 14px", borderRadius: 999, border: `1.5px solid ${RED}`, background: "transparent", color: RED, fontWeight: 800, fontSize: 12.5, cursor: "pointer" },
  badgeDot: { position: "absolute", top: -6, right: -6, minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, background: RED, color: "#fff", fontSize: 11, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" },
};

if (typeof document !== "undefined" && !document.getElementById("carnet-kf")) {
  const el = document.createElement("style");
  el.id = "carnet-kf";
  el.textContent = "@keyframes carnetSlide{from{transform:translateX(-100%)}to{transform:translateX(0)}}" +
    "@media(max-width:640px){.carnet-bartop{flex-direction:column;align-items:stretch;gap:8px}.carnet-actions{width:100%;flex-wrap:wrap;gap:8px}.carnet-actions button{flex:1 1 calc(50% - 4px) !important;min-width:0 !important;justify-content:center;padding:10px 8px !important;font-size:13px !important}}";
  document.head.appendChild(el);
}
